import { initNativeScroll } from "./native-scroll-snap-Cd15iSfl.js";
import { getThemeSettings, onFirstInteraction } from "./utilities-DtVOIXIY.js";
import "./waapi-patch-BCt4pEht.js";
import "./cookie-banner-layout-DZP7Q0uT.js";
import "./section-anchor-scroll-Bvm-mXVl.js";
import "./mobile-anim-gate-Btn1wVuD.js";
function isNumber(subject) {
  return typeof subject === "number";
}
function isString(subject) {
  return typeof subject === "string";
}
function isBoolean(subject) {
  return typeof subject === "boolean";
}
function isObject(subject) {
  return Object.prototype.toString.call(subject) === "[object Object]";
}
function mathAbs(n) {
  return Math.abs(n);
}
function mathSign(n) {
  return Math.sign(n);
}
function deltaAbs(valueB, valueA) {
  return mathAbs(valueB - valueA);
}
function factorAbs(valueB, valueA) {
  if (valueB === 0 || valueA === 0) return 0;
  if (mathAbs(valueB) <= mathAbs(valueA)) return 0;
  const diff = deltaAbs(mathAbs(valueB), mathAbs(valueA));
  return mathAbs(diff / valueB);
}
function roundToTwoDecimals(num) {
  return Math.round(num * 100) / 100;
}
function arrayKeys(array) {
  return objectKeys(array).map(Number);
}
function arrayLast(array) {
  return array[arrayLastIndex(array)];
}
function arrayLastIndex(array) {
  return Math.max(0, array.length - 1);
}
function arrayIsLastIndex(array, index) {
  return index === arrayLastIndex(array);
}
function arrayFromNumber(n, startAt = 0) {
  return Array.from(Array(n), (_, i) => startAt + i);
}
function objectKeys(object) {
  return Object.keys(object);
}
function objectsMergeDeep(objectA, objectB) {
  return [objectA, objectB].reduce((mergedObjects, currentObject) => {
    objectKeys(currentObject).forEach((key) => {
      const valueA = mergedObjects[key];
      const valueB = currentObject[key];
      const areObjects = isObject(valueA) && isObject(valueB);
      mergedObjects[key] = areObjects ? objectsMergeDeep(valueA, valueB) : valueB;
    });
    return mergedObjects;
  }, {});
}
function isMouseEvent(evt, ownerWindow) {
  return typeof ownerWindow.MouseEvent !== "undefined" && evt instanceof ownerWindow.MouseEvent;
}
function Alignment(align, viewSize) {
  const predefined = {
    start,
    center,
    end
  };
  function start() {
    return 0;
  }
  function center(n) {
    return end(n) / 2;
  }
  function end(n) {
    return viewSize - n;
  }
  function measure(n, index) {
    if (isString(align)) return predefined[align](n);
    return align(viewSize, n, index);
  }
  const self = {
    measure
  };
  return self;
}
function EventStore() {
  let listeners = [];
  function add(node, type, handler, options = {
    passive: true
  }) {
    let removeListener;
    if ("addEventListener" in node) {
      node.addEventListener(type, handler, options);
      removeListener = () => node.removeEventListener(type, handler, options);
    } else {
      const legacyMediaQueryList = node;
      legacyMediaQueryList.addListener(handler);
      removeListener = () => legacyMediaQueryList.removeListener(handler);
    }
    listeners.push(removeListener);
    return self;
  }
  function clear() {
    listeners = listeners.filter((remove) => remove());
  }
  const self = {
    add,
    clear
  };
  return self;
}
function Animations(ownerDocument, ownerWindow, update, render) {
  const documentVisibleHandler = EventStore();
  const fixedTimeStep = 1e3 / 60;
  let lastTimeStamp = null;
  let accumulatedTime = 0;
  let animationId = 0;
  function init() {
    documentVisibleHandler.add(ownerDocument, "visibilitychange", () => {
      if (ownerDocument.hidden) reset();
    });
  }
  function destroy() {
    stop();
    documentVisibleHandler.clear();
  }
  function animate(timeStamp) {
    if (!animationId) return;
    if (!lastTimeStamp) {
      lastTimeStamp = timeStamp;
      update();
      update();
    }
    const timeElapsed = timeStamp - lastTimeStamp;
    lastTimeStamp = timeStamp;
    accumulatedTime += timeElapsed;
    while (accumulatedTime >= fixedTimeStep) {
      update();
      accumulatedTime -= fixedTimeStep;
    }
    const alpha = accumulatedTime / fixedTimeStep;
    render(alpha);
    if (animationId) {
      animationId = ownerWindow.requestAnimationFrame(animate);
    }
  }
  function start() {
    if (animationId) return;
    animationId = ownerWindow.requestAnimationFrame(animate);
  }
  function stop() {
    ownerWindow.cancelAnimationFrame(animationId);
    lastTimeStamp = null;
    accumulatedTime = 0;
    animationId = 0;
  }
  function reset() {
    lastTimeStamp = null;
    accumulatedTime = 0;
  }
  const self = {
    init,
    destroy,
    start,
    stop,
    update,
    render
  };
  return self;
}
function Axis(axis, contentDirection) {
  const isRightToLeft = contentDirection === "rtl";
  const isVertical = axis === "y";
  const scroll = isVertical ? "y" : "x";
  const cross = isVertical ? "x" : "y";
  const sign = !isVertical && isRightToLeft ? -1 : 1;
  const startEdge = getStartEdge();
  const endEdge = getEndEdge();
  function measureSize(nodeRect) {
    const {
      height,
      width
    } = nodeRect;
    return isVertical ? height : width;
  }
  function getStartEdge() {
    if (isVertical) return "top";
    return isRightToLeft ? "right" : "left";
  }
  function getEndEdge() {
    if (isVertical) return "bottom";
    return isRightToLeft ? "left" : "right";
  }
  function direction(n) {
    return n * sign;
  }
  const self = {
    scroll,
    cross,
    startEdge,
    endEdge,
    measureSize,
    direction
  };
  return self;
}
function Limit(min = 0, max = 0) {
  const length = mathAbs(min - max);
  function reachedMin(n) {
    return n < min;
  }
  function reachedMax(n) {
    return n > max;
  }
  function reachedAny(n) {
    return reachedMin(n) || reachedMax(n);
  }
  function constrain(n) {
    if (!reachedAny(n)) return n;
    return reachedMin(n) ? min : max;
  }
  function removeOffset(n) {
    if (!length) return n;
    return n - length * Math.ceil((n - max) / length);
  }
  const self = {
    length,
    max,
    min,
    constrain,
    reachedAny,
    reachedMax,
    reachedMin,
    removeOffset
  };
  return self;
}
function Counter(max, start, loop) {
  const {
    constrain
  } = Limit(0, max);
  const loopEnd = max + 1;
  let counter = withinLimit(start);
  function withinLimit(n) {
    return !loop ? constrain(n) : mathAbs((loopEnd + n) % loopEnd);
  }
  function get() {
    return counter;
  }
  function set(n) {
    counter = withinLimit(n);
    return self;
  }
  function add(n) {
    return clone().set(get() + n);
  }
  function clone() {
    return Counter(max, get(), loop);
  }
  const self = {
    get,
    set,
    add,
    clone
  };
  return self;
}
function DragHandler(axis, rootNode, ownerDocument, ownerWindow, target, dragTracker, location, animation, scrollTo, scrollBody, scrollTarget, index, eventHandler, percentOfView, dragFree, dragThreshold, skipSnaps, baseFriction, watchDrag) {
  const {
    cross: crossAxis,
    direction
  } = axis;
  const focusNodes = ["INPUT", "SELECT", "TEXTAREA"];
  const nonPassiveEvent = {
    passive: false
  };
  const initEvents = EventStore();
  const dragEvents = EventStore();
  const goToNextThreshold = Limit(50, 225).constrain(percentOfView.measure(20));
  const snapForceBoost = {
    mouse: 300,
    touch: 400
  };
  const freeForceBoost = {
    mouse: 500,
    touch: 600
  };
  const baseSpeed = dragFree ? 43 : 25;
  let isMoving = false;
  let startScroll = 0;
  let startCross = 0;
  let pointerIsDown = false;
  let preventScroll = false;
  let preventClick = false;
  let isMouse = false;
  function init(emblaApi) {
    if (!watchDrag) return;
    function downIfAllowed(evt) {
      if (isBoolean(watchDrag) || watchDrag(emblaApi, evt)) down(evt);
    }
    const node = rootNode;
    initEvents.add(node, "dragstart", (evt) => evt.preventDefault(), nonPassiveEvent).add(node, "touchmove", () => void 0, nonPassiveEvent).add(node, "touchend", () => void 0).add(node, "touchstart", downIfAllowed).add(node, "mousedown", downIfAllowed).add(node, "touchcancel", up).add(node, "contextmenu", up).add(node, "click", click, true);
  }
  function destroy() {
    initEvents.clear();
    dragEvents.clear();
  }
  function addDragEvents() {
    const node = isMouse ? ownerDocument : rootNode;
    dragEvents.add(node, "touchmove", move, nonPassiveEvent).add(node, "touchend", up).add(node, "mousemove", move, nonPassiveEvent).add(node, "mouseup", up);
  }
  function isFocusNode(node) {
    const nodeName = node.nodeName || "";
    return focusNodes.includes(nodeName);
  }
  function forceBoost() {
    const boost = dragFree ? freeForceBoost : snapForceBoost;
    const type = isMouse ? "mouse" : "touch";
    return boost[type];
  }
  function allowedForce(force, targetChanged) {
    const next = index.add(mathSign(force) * -1);
    const baseForce = scrollTarget.byDistance(force, !dragFree).distance;
    if (dragFree || mathAbs(force) < goToNextThreshold) return baseForce;
    if (skipSnaps && targetChanged) return baseForce * 0.5;
    return scrollTarget.byIndex(next.get(), 0).distance;
  }
  function down(evt) {
    const isMouseEvt = isMouseEvent(evt, ownerWindow);
    isMouse = isMouseEvt;
    preventClick = dragFree && isMouseEvt && !evt.buttons && isMoving;
    isMoving = deltaAbs(target.get(), location.get()) >= 2;
    if (isMouseEvt && evt.button !== 0) return;
    if (isFocusNode(evt.target)) return;
    pointerIsDown = true;
    dragTracker.pointerDown(evt);
    scrollBody.useFriction(0).useDuration(0);
    target.set(location);
    addDragEvents();
    startScroll = dragTracker.readPoint(evt);
    startCross = dragTracker.readPoint(evt, crossAxis);
    eventHandler.emit("pointerDown");
  }
  function move(evt) {
    const isTouchEvt = !isMouseEvent(evt, ownerWindow);
    if (isTouchEvt && evt.touches.length >= 2) return up(evt);
    const lastScroll = dragTracker.readPoint(evt);
    const lastCross = dragTracker.readPoint(evt, crossAxis);
    const diffScroll = deltaAbs(lastScroll, startScroll);
    const diffCross = deltaAbs(lastCross, startCross);
    if (!preventScroll && !isMouse) {
      if (!evt.cancelable) return up(evt);
      preventScroll = diffScroll > diffCross;
      if (!preventScroll) return up(evt);
    }
    const diff = dragTracker.pointerMove(evt);
    if (diffScroll > dragThreshold) preventClick = true;
    scrollBody.useFriction(0.3).useDuration(0.75);
    animation.start();
    target.add(direction(diff));
    evt.preventDefault();
  }
  function up(evt) {
    const currentLocation = scrollTarget.byDistance(0, false);
    const targetChanged = currentLocation.index !== index.get();
    const rawForce = dragTracker.pointerUp(evt) * forceBoost();
    const force = allowedForce(direction(rawForce), targetChanged);
    const forceFactor = factorAbs(rawForce, force);
    const speed = baseSpeed - 10 * forceFactor;
    const friction = baseFriction + forceFactor / 50;
    preventScroll = false;
    pointerIsDown = false;
    dragEvents.clear();
    scrollBody.useDuration(speed).useFriction(friction);
    scrollTo.distance(force, !dragFree);
    isMouse = false;
    eventHandler.emit("pointerUp");
  }
  function click(evt) {
    if (preventClick) {
      evt.stopPropagation();
      evt.preventDefault();
      preventClick = false;
    }
  }
  function pointerDown() {
    return pointerIsDown;
  }
  const self = {
    init,
    destroy,
    pointerDown
  };
  return self;
}
function DragTracker(axis, ownerWindow) {
  const logInterval = 170;
  let startEvent;
  let lastEvent;
  function readTime(evt) {
    return evt.timeStamp;
  }
  function readPoint(evt, evtAxis) {
    const property = evtAxis || axis.scroll;
    const coord = `client${property === "x" ? "X" : "Y"}`;
    return (isMouseEvent(evt, ownerWindow) ? evt : evt.touches[0])[coord];
  }
  function pointerDown(evt) {
    startEvent = evt;
    lastEvent = evt;
    return readPoint(evt);
  }
  function pointerMove(evt) {
    const diff = readPoint(evt) - readPoint(lastEvent);
    const expired = readTime(evt) - readTime(startEvent) > logInterval;
    lastEvent = evt;
    if (expired) startEvent = evt;
    return diff;
  }
  function pointerUp(evt) {
    if (!startEvent || !lastEvent) return 0;
    const diffDrag = readPoint(lastEvent) - readPoint(startEvent);
    const diffTime = readTime(evt) - readTime(startEvent);
    const expired = readTime(evt) - readTime(lastEvent) > logInterval;
    const force = diffDrag / diffTime;
    const isFlick = diffTime && !expired && mathAbs(force) > 0.1;
    return isFlick ? force : 0;
  }
  const self = {
    pointerDown,
    pointerMove,
    pointerUp,
    readPoint
  };
  return self;
}
function NodeRects() {
  function measure(node) {
    const {
      offsetTop,
      offsetLeft,
      offsetWidth,
      offsetHeight
    } = node;
    const offset = {
      top: offsetTop,
      right: offsetLeft + offsetWidth,
      bottom: offsetTop + offsetHeight,
      left: offsetLeft,
      width: offsetWidth,
      height: offsetHeight
    };
    return offset;
  }
  const self = {
    measure
  };
  return self;
}
function PercentOfView(viewSize) {
  function measure(n) {
    return viewSize * (n / 100);
  }
  const self = {
    measure
  };
  return self;
}
function ResizeHandler(container, eventHandler, ownerWindow, slides, axis, watchResize, nodeRects) {
  const observeNodes = [container].concat(slides);
  let resizeObserver;
  let containerSize;
  let slideSizes = [];
  let destroyed = false;
  function readSize(node) {
    return axis.measureSize(nodeRects.measure(node));
  }
  function init(emblaApi) {
    if (!watchResize) return;
    containerSize = readSize(container);
    slideSizes = slides.map(readSize);
    function defaultCallback(entries) {
      for (const entry of entries) {
        if (destroyed) return;
        const isContainer = entry.target === container;
        const slideIndex = slides.indexOf(entry.target);
        const lastSize = isContainer ? containerSize : slideSizes[slideIndex];
        const newSize = readSize(isContainer ? container : slides[slideIndex]);
        const diffSize = mathAbs(newSize - lastSize);
        if (diffSize >= 0.5) {
          emblaApi.reInit();
          eventHandler.emit("resize");
          break;
        }
      }
    }
    resizeObserver = new ResizeObserver((entries) => {
      if (isBoolean(watchResize) || watchResize(emblaApi, entries)) {
        defaultCallback(entries);
      }
    });
    ownerWindow.requestAnimationFrame(() => {
      observeNodes.forEach((node) => resizeObserver.observe(node));
    });
  }
  function destroy() {
    destroyed = true;
    if (resizeObserver) resizeObserver.disconnect();
  }
  const self = {
    init,
    destroy
  };
  return self;
}
function ScrollBody(location, offsetLocation, previousLocation, target, baseDuration, baseFriction) {
  let scrollVelocity = 0;
  let scrollDirection = 0;
  let scrollDuration = baseDuration;
  let scrollFriction = baseFriction;
  let rawLocation = location.get();
  let rawLocationPrevious = 0;
  function seek() {
    const displacement = target.get() - location.get();
    const isInstant = !scrollDuration;
    let scrollDistance = 0;
    if (isInstant) {
      scrollVelocity = 0;
      previousLocation.set(target);
      location.set(target);
      scrollDistance = displacement;
    } else {
      previousLocation.set(location);
      scrollVelocity += displacement / scrollDuration;
      scrollVelocity *= scrollFriction;
      rawLocation += scrollVelocity;
      location.add(scrollVelocity);
      scrollDistance = rawLocation - rawLocationPrevious;
    }
    scrollDirection = mathSign(scrollDistance);
    rawLocationPrevious = rawLocation;
    return self;
  }
  function settled() {
    const diff = target.get() - offsetLocation.get();
    return mathAbs(diff) < 1e-3;
  }
  function duration() {
    return scrollDuration;
  }
  function direction() {
    return scrollDirection;
  }
  function velocity() {
    return scrollVelocity;
  }
  function useBaseDuration() {
    return useDuration(baseDuration);
  }
  function useBaseFriction() {
    return useFriction(baseFriction);
  }
  function useDuration(n) {
    scrollDuration = n;
    return self;
  }
  function useFriction(n) {
    scrollFriction = n;
    return self;
  }
  const self = {
    direction,
    duration,
    velocity,
    seek,
    settled,
    useBaseFriction,
    useBaseDuration,
    useFriction,
    useDuration
  };
  return self;
}
function ScrollBounds(limit, location, target, scrollBody, percentOfView) {
  const pullBackThreshold = percentOfView.measure(10);
  const edgeOffsetTolerance = percentOfView.measure(50);
  const frictionLimit = Limit(0.1, 0.99);
  let disabled = false;
  function shouldConstrain() {
    if (disabled) return false;
    if (!limit.reachedAny(target.get())) return false;
    if (!limit.reachedAny(location.get())) return false;
    return true;
  }
  function constrain(pointerDown) {
    if (!shouldConstrain()) return;
    const edge = limit.reachedMin(location.get()) ? "min" : "max";
    const diffToEdge = mathAbs(limit[edge] - location.get());
    const diffToTarget = target.get() - location.get();
    const friction = frictionLimit.constrain(diffToEdge / edgeOffsetTolerance);
    target.subtract(diffToTarget * friction);
    if (!pointerDown && mathAbs(diffToTarget) < pullBackThreshold) {
      target.set(limit.constrain(target.get()));
      scrollBody.useDuration(25).useBaseFriction();
    }
  }
  function toggleActive(active) {
    disabled = !active;
  }
  const self = {
    shouldConstrain,
    constrain,
    toggleActive
  };
  return self;
}
function ScrollContain(viewSize, contentSize, snapsAligned, containScroll, pixelTolerance) {
  const scrollBounds = Limit(-contentSize + viewSize, 0);
  const snapsBounded = measureBounded();
  const scrollContainLimit = findScrollContainLimit();
  const snapsContained = measureContained();
  function usePixelTolerance(bound, snap) {
    return deltaAbs(bound, snap) <= 1;
  }
  function findScrollContainLimit() {
    const startSnap = snapsBounded[0];
    const endSnap = arrayLast(snapsBounded);
    const min = snapsBounded.lastIndexOf(startSnap);
    const max = snapsBounded.indexOf(endSnap) + 1;
    return Limit(min, max);
  }
  function measureBounded() {
    return snapsAligned.map((snapAligned, index) => {
      const {
        min,
        max
      } = scrollBounds;
      const snap = scrollBounds.constrain(snapAligned);
      const isFirst = !index;
      const isLast = arrayIsLastIndex(snapsAligned, index);
      if (isFirst) return max;
      if (isLast) return min;
      if (usePixelTolerance(min, snap)) return min;
      if (usePixelTolerance(max, snap)) return max;
      return snap;
    }).map((scrollBound) => parseFloat(scrollBound.toFixed(3)));
  }
  function measureContained() {
    if (contentSize <= viewSize + pixelTolerance) return [scrollBounds.max];
    if (containScroll === "keepSnaps") return snapsBounded;
    const {
      min,
      max
    } = scrollContainLimit;
    return snapsBounded.slice(min, max);
  }
  const self = {
    snapsContained,
    scrollContainLimit
  };
  return self;
}
function ScrollLimit(contentSize, scrollSnaps, loop) {
  const max = scrollSnaps[0];
  const min = loop ? max - contentSize : arrayLast(scrollSnaps);
  const limit = Limit(min, max);
  const self = {
    limit
  };
  return self;
}
function ScrollLooper(contentSize, limit, location, vectors) {
  const jointSafety = 0.1;
  const min = limit.min + jointSafety;
  const max = limit.max + jointSafety;
  const {
    reachedMin,
    reachedMax
  } = Limit(min, max);
  function shouldLoop(direction) {
    if (direction === 1) return reachedMax(location.get());
    if (direction === -1) return reachedMin(location.get());
    return false;
  }
  function loop(direction) {
    if (!shouldLoop(direction)) return;
    const loopDistance = contentSize * (direction * -1);
    vectors.forEach((v) => v.add(loopDistance));
  }
  const self = {
    loop
  };
  return self;
}
function ScrollProgress(limit) {
  const {
    max,
    length
  } = limit;
  function get(n) {
    const currentLocation = n - max;
    return length ? currentLocation / -length : 0;
  }
  const self = {
    get
  };
  return self;
}
function ScrollSnaps(axis, alignment, containerRect, slideRects, slidesToScroll) {
  const {
    startEdge,
    endEdge
  } = axis;
  const {
    groupSlides
  } = slidesToScroll;
  const alignments = measureSizes().map(alignment.measure);
  const snaps = measureUnaligned();
  const snapsAligned = measureAligned();
  function measureSizes() {
    return groupSlides(slideRects).map((rects) => arrayLast(rects)[endEdge] - rects[0][startEdge]).map(mathAbs);
  }
  function measureUnaligned() {
    return slideRects.map((rect) => containerRect[startEdge] - rect[startEdge]).map((snap) => -mathAbs(snap));
  }
  function measureAligned() {
    return groupSlides(snaps).map((g) => g[0]).map((snap, index) => snap + alignments[index]);
  }
  const self = {
    snaps,
    snapsAligned
  };
  return self;
}
function SlideRegistry(containSnaps, containScroll, scrollSnaps, scrollContainLimit, slidesToScroll, slideIndexes) {
  const {
    groupSlides
  } = slidesToScroll;
  const {
    min,
    max
  } = scrollContainLimit;
  const slideRegistry = createSlideRegistry();
  function createSlideRegistry() {
    const groupedSlideIndexes = groupSlides(slideIndexes);
    const doNotContain = !containSnaps || containScroll === "keepSnaps";
    if (scrollSnaps.length === 1) return [slideIndexes];
    if (doNotContain) return groupedSlideIndexes;
    return groupedSlideIndexes.slice(min, max).map((group, index, groups) => {
      const isFirst = !index;
      const isLast = arrayIsLastIndex(groups, index);
      if (isFirst) {
        const range = arrayLast(groups[0]) + 1;
        return arrayFromNumber(range);
      }
      if (isLast) {
        const range = arrayLastIndex(slideIndexes) - arrayLast(groups)[0] + 1;
        return arrayFromNumber(range, arrayLast(groups)[0]);
      }
      return group;
    });
  }
  const self = {
    slideRegistry
  };
  return self;
}
function ScrollTarget(loop, scrollSnaps, contentSize, limit, targetVector) {
  const {
    reachedAny,
    removeOffset,
    constrain
  } = limit;
  function minDistance(distances) {
    return distances.concat().sort((a, b) => mathAbs(a) - mathAbs(b))[0];
  }
  function findTargetSnap(target) {
    const distance = loop ? removeOffset(target) : constrain(target);
    const ascDiffsToSnaps = scrollSnaps.map((snap, index2) => ({
      diff: shortcut(snap - distance, 0),
      index: index2
    })).sort((d1, d2) => mathAbs(d1.diff) - mathAbs(d2.diff));
    const {
      index
    } = ascDiffsToSnaps[0];
    return {
      index,
      distance
    };
  }
  function shortcut(target, direction) {
    const targets = [target, target + contentSize, target - contentSize];
    if (!loop) return target;
    if (!direction) return minDistance(targets);
    const matchingTargets = targets.filter((t) => mathSign(t) === direction);
    if (matchingTargets.length) return minDistance(matchingTargets);
    return arrayLast(targets) - contentSize;
  }
  function byIndex(index, direction) {
    const diffToSnap = scrollSnaps[index] - targetVector.get();
    const distance = shortcut(diffToSnap, direction);
    return {
      index,
      distance
    };
  }
  function byDistance(distance, snap) {
    const target = targetVector.get() + distance;
    const {
      index,
      distance: targetSnapDistance
    } = findTargetSnap(target);
    const reachedBound = !loop && reachedAny(target);
    if (!snap || reachedBound) return {
      index,
      distance
    };
    const diffToSnap = scrollSnaps[index] - targetSnapDistance;
    const snapDistance = distance + shortcut(diffToSnap, 0);
    return {
      index,
      distance: snapDistance
    };
  }
  const self = {
    byDistance,
    byIndex,
    shortcut
  };
  return self;
}
function ScrollTo(animation, indexCurrent, indexPrevious, scrollBody, scrollTarget, targetVector, eventHandler) {
  function scrollTo(target) {
    const distanceDiff = target.distance;
    const indexDiff = target.index !== indexCurrent.get();
    targetVector.add(distanceDiff);
    if (distanceDiff) {
      if (scrollBody.duration()) {
        animation.start();
      } else {
        animation.update();
        animation.render(1);
        animation.update();
      }
    }
    if (indexDiff) {
      indexPrevious.set(indexCurrent.get());
      indexCurrent.set(target.index);
      eventHandler.emit("select");
    }
  }
  function distance(n, snap) {
    const target = scrollTarget.byDistance(n, snap);
    scrollTo(target);
  }
  function index(n, direction) {
    const targetIndex = indexCurrent.clone().set(n);
    const target = scrollTarget.byIndex(targetIndex.get(), direction);
    scrollTo(target);
  }
  const self = {
    distance,
    index
  };
  return self;
}
function SlideFocus(root, slides, slideRegistry, scrollTo, scrollBody, eventStore, eventHandler, watchFocus) {
  const focusListenerOptions = {
    passive: true,
    capture: true
  };
  let lastTabPressTime = 0;
  function init(emblaApi) {
    if (!watchFocus) return;
    function defaultCallback(index) {
      const nowTime = (/* @__PURE__ */ new Date()).getTime();
      const diffTime = nowTime - lastTabPressTime;
      if (diffTime > 10) return;
      eventHandler.emit("slideFocusStart");
      root.scrollLeft = 0;
      const group = slideRegistry.findIndex((group2) => group2.includes(index));
      if (!isNumber(group)) return;
      scrollBody.useDuration(0);
      scrollTo.index(group, 0);
      eventHandler.emit("slideFocus");
    }
    eventStore.add(document, "keydown", registerTabPress, false);
    slides.forEach((slide, slideIndex) => {
      eventStore.add(slide, "focus", (evt) => {
        if (isBoolean(watchFocus) || watchFocus(emblaApi, evt)) {
          defaultCallback(slideIndex);
        }
      }, focusListenerOptions);
    });
  }
  function registerTabPress(event) {
    if (event.code === "Tab") lastTabPressTime = (/* @__PURE__ */ new Date()).getTime();
  }
  const self = {
    init
  };
  return self;
}
function Vector1D(initialValue) {
  let value = initialValue;
  function get() {
    return value;
  }
  function set(n) {
    value = normalizeInput(n);
  }
  function add(n) {
    value += normalizeInput(n);
  }
  function subtract(n) {
    value -= normalizeInput(n);
  }
  function normalizeInput(n) {
    return isNumber(n) ? n : n.get();
  }
  const self = {
    get,
    set,
    add,
    subtract
  };
  return self;
}
function Translate(axis, container) {
  const translate = axis.scroll === "x" ? x : y;
  const containerStyle = container.style;
  let previousTarget = null;
  let disabled = false;
  function x(n) {
    return `translate3d(${n}px,0px,0px)`;
  }
  function y(n) {
    return `translate3d(0px,${n}px,0px)`;
  }
  function to(target) {
    if (disabled) return;
    const newTarget = roundToTwoDecimals(axis.direction(target));
    if (newTarget === previousTarget) return;
    containerStyle.transform = translate(newTarget);
    previousTarget = newTarget;
  }
  function toggleActive(active) {
    disabled = !active;
  }
  function clear() {
    if (disabled) return;
    containerStyle.transform = "";
    if (!container.getAttribute("style")) container.removeAttribute("style");
  }
  const self = {
    clear,
    to,
    toggleActive
  };
  return self;
}
function SlideLooper(axis, viewSize, contentSize, slideSizes, slideSizesWithGaps, snaps, scrollSnaps, location, slides) {
  const roundingSafety = 0.5;
  const ascItems = arrayKeys(slideSizesWithGaps);
  const descItems = arrayKeys(slideSizesWithGaps).reverse();
  const loopPoints = startPoints().concat(endPoints());
  function removeSlideSizes(indexes, from) {
    return indexes.reduce((a, i) => {
      return a - slideSizesWithGaps[i];
    }, from);
  }
  function slidesInGap(indexes, gap) {
    return indexes.reduce((a, i) => {
      const remainingGap = removeSlideSizes(a, gap);
      return remainingGap > 0 ? a.concat([i]) : a;
    }, []);
  }
  function findSlideBounds(offset) {
    return snaps.map((snap, index) => ({
      start: snap - slideSizes[index] + roundingSafety + offset,
      end: snap + viewSize - roundingSafety + offset
    }));
  }
  function findLoopPoints(indexes, offset, isEndEdge) {
    const slideBounds = findSlideBounds(offset);
    return indexes.map((index) => {
      const initial = isEndEdge ? 0 : -contentSize;
      const altered = isEndEdge ? contentSize : 0;
      const boundEdge = isEndEdge ? "end" : "start";
      const loopPoint = slideBounds[index][boundEdge];
      return {
        index,
        loopPoint,
        slideLocation: Vector1D(-1),
        translate: Translate(axis, slides[index]),
        target: () => location.get() > loopPoint ? initial : altered
      };
    });
  }
  function startPoints() {
    const gap = scrollSnaps[0];
    const indexes = slidesInGap(descItems, gap);
    return findLoopPoints(indexes, contentSize, false);
  }
  function endPoints() {
    const gap = viewSize - scrollSnaps[0] - 1;
    const indexes = slidesInGap(ascItems, gap);
    return findLoopPoints(indexes, -contentSize, true);
  }
  function canLoop() {
    return loopPoints.every(({
      index
    }) => {
      const otherIndexes = ascItems.filter((i) => i !== index);
      return removeSlideSizes(otherIndexes, viewSize) <= 0.1;
    });
  }
  function loop() {
    loopPoints.forEach((loopPoint) => {
      const {
        target,
        translate,
        slideLocation
      } = loopPoint;
      const shiftLocation = target();
      if (shiftLocation === slideLocation.get()) return;
      translate.to(shiftLocation);
      slideLocation.set(shiftLocation);
    });
  }
  function clear() {
    loopPoints.forEach((loopPoint) => loopPoint.translate.clear());
  }
  const self = {
    canLoop,
    clear,
    loop,
    loopPoints
  };
  return self;
}
function SlidesHandler(container, eventHandler, watchSlides) {
  let mutationObserver;
  let destroyed = false;
  function init(emblaApi) {
    if (!watchSlides) return;
    function defaultCallback(mutations) {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          emblaApi.reInit();
          eventHandler.emit("slidesChanged");
          break;
        }
      }
    }
    mutationObserver = new MutationObserver((mutations) => {
      if (destroyed) return;
      if (isBoolean(watchSlides) || watchSlides(emblaApi, mutations)) {
        defaultCallback(mutations);
      }
    });
    mutationObserver.observe(container, {
      childList: true
    });
  }
  function destroy() {
    if (mutationObserver) mutationObserver.disconnect();
    destroyed = true;
  }
  const self = {
    init,
    destroy
  };
  return self;
}
function SlidesInView(container, slides, eventHandler, threshold) {
  const intersectionEntryMap = {};
  let inViewCache = null;
  let notInViewCache = null;
  let intersectionObserver;
  let destroyed = false;
  function init() {
    intersectionObserver = new IntersectionObserver((entries) => {
      if (destroyed) return;
      entries.forEach((entry) => {
        const index = slides.indexOf(entry.target);
        intersectionEntryMap[index] = entry;
      });
      inViewCache = null;
      notInViewCache = null;
      eventHandler.emit("slidesInView");
    }, {
      root: container.parentElement,
      threshold
    });
    slides.forEach((slide) => intersectionObserver.observe(slide));
  }
  function destroy() {
    if (intersectionObserver) intersectionObserver.disconnect();
    destroyed = true;
  }
  function createInViewList(inView) {
    return objectKeys(intersectionEntryMap).reduce((list, slideIndex) => {
      const index = parseInt(slideIndex);
      const {
        isIntersecting
      } = intersectionEntryMap[index];
      const inViewMatch = inView && isIntersecting;
      const notInViewMatch = !inView && !isIntersecting;
      if (inViewMatch || notInViewMatch) list.push(index);
      return list;
    }, []);
  }
  function get(inView = true) {
    if (inView && inViewCache) return inViewCache;
    if (!inView && notInViewCache) return notInViewCache;
    const slideIndexes = createInViewList(inView);
    if (inView) inViewCache = slideIndexes;
    if (!inView) notInViewCache = slideIndexes;
    return slideIndexes;
  }
  const self = {
    init,
    destroy,
    get
  };
  return self;
}
function SlideSizes(axis, containerRect, slideRects, slides, readEdgeGap, ownerWindow) {
  const {
    measureSize,
    startEdge,
    endEdge
  } = axis;
  const withEdgeGap = slideRects[0] && readEdgeGap;
  const startGap = measureStartGap();
  const endGap = measureEndGap();
  const slideSizes = slideRects.map(measureSize);
  const slideSizesWithGaps = measureWithGaps();
  function measureStartGap() {
    if (!withEdgeGap) return 0;
    const slideRect = slideRects[0];
    return mathAbs(containerRect[startEdge] - slideRect[startEdge]);
  }
  function measureEndGap() {
    if (!withEdgeGap) return 0;
    const style = ownerWindow.getComputedStyle(arrayLast(slides));
    return parseFloat(style.getPropertyValue(`margin-${endEdge}`));
  }
  function measureWithGaps() {
    return slideRects.map((rect, index, rects) => {
      const isFirst = !index;
      const isLast = arrayIsLastIndex(rects, index);
      if (isFirst) return slideSizes[index] + startGap;
      if (isLast) return slideSizes[index] + endGap;
      return rects[index + 1][startEdge] - rect[startEdge];
    }).map(mathAbs);
  }
  const self = {
    slideSizes,
    slideSizesWithGaps,
    startGap,
    endGap
  };
  return self;
}
function SlidesToScroll(axis, viewSize, slidesToScroll, loop, containerRect, slideRects, startGap, endGap, pixelTolerance) {
  const {
    startEdge,
    endEdge,
    direction
  } = axis;
  const groupByNumber = isNumber(slidesToScroll);
  function byNumber(array, groupSize) {
    return arrayKeys(array).filter((i) => i % groupSize === 0).map((i) => array.slice(i, i + groupSize));
  }
  function bySize(array) {
    if (!array.length) return [];
    return arrayKeys(array).reduce((groups, rectB, index) => {
      const rectA = arrayLast(groups) || 0;
      const isFirst = rectA === 0;
      const isLast = rectB === arrayLastIndex(array);
      const edgeA = containerRect[startEdge] - slideRects[rectA][startEdge];
      const edgeB = containerRect[startEdge] - slideRects[rectB][endEdge];
      const gapA = !loop && isFirst ? direction(startGap) : 0;
      const gapB = !loop && isLast ? direction(endGap) : 0;
      const chunkSize = mathAbs(edgeB - gapB - (edgeA + gapA));
      if (index && chunkSize > viewSize + pixelTolerance) groups.push(rectB);
      if (isLast) groups.push(array.length);
      return groups;
    }, []).map((currentSize, index, groups) => {
      const previousSize = Math.max(groups[index - 1] || 0);
      return array.slice(previousSize, currentSize);
    });
  }
  function groupSlides(array) {
    return groupByNumber ? byNumber(array, slidesToScroll) : bySize(array);
  }
  const self = {
    groupSlides
  };
  return self;
}
function Engine(root, container, slides, ownerDocument, ownerWindow, options, eventHandler) {
  const {
    align,
    axis: scrollAxis,
    direction,
    startIndex,
    loop,
    duration,
    dragFree,
    dragThreshold,
    inViewThreshold,
    slidesToScroll: groupSlides,
    skipSnaps,
    containScroll,
    watchResize,
    watchSlides,
    watchDrag,
    watchFocus
  } = options;
  const pixelTolerance = 2;
  const nodeRects = NodeRects();
  const containerRect = nodeRects.measure(container);
  const slideRects = slides.map(nodeRects.measure);
  const axis = Axis(scrollAxis, direction);
  const viewSize = axis.measureSize(containerRect);
  const percentOfView = PercentOfView(viewSize);
  const alignment = Alignment(align, viewSize);
  const containSnaps = !loop && !!containScroll;
  const readEdgeGap = loop || !!containScroll;
  const {
    slideSizes,
    slideSizesWithGaps,
    startGap,
    endGap
  } = SlideSizes(axis, containerRect, slideRects, slides, readEdgeGap, ownerWindow);
  const slidesToScroll = SlidesToScroll(axis, viewSize, groupSlides, loop, containerRect, slideRects, startGap, endGap, pixelTolerance);
  const {
    snaps,
    snapsAligned
  } = ScrollSnaps(axis, alignment, containerRect, slideRects, slidesToScroll);
  const contentSize = -arrayLast(snaps) + arrayLast(slideSizesWithGaps);
  const {
    snapsContained,
    scrollContainLimit
  } = ScrollContain(viewSize, contentSize, snapsAligned, containScroll, pixelTolerance);
  const scrollSnaps = containSnaps ? snapsContained : snapsAligned;
  const {
    limit
  } = ScrollLimit(contentSize, scrollSnaps, loop);
  const index = Counter(arrayLastIndex(scrollSnaps), startIndex, loop);
  const indexPrevious = index.clone();
  const slideIndexes = arrayKeys(slides);
  const update = ({
    dragHandler,
    scrollBody: scrollBody2,
    scrollBounds,
    options: {
      loop: loop2
    }
  }) => {
    if (!loop2) scrollBounds.constrain(dragHandler.pointerDown());
    scrollBody2.seek();
  };
  const render = ({
    scrollBody: scrollBody2,
    translate,
    location: location2,
    offsetLocation: offsetLocation2,
    previousLocation: previousLocation2,
    scrollLooper,
    slideLooper,
    dragHandler,
    animation: animation2,
    eventHandler: eventHandler2,
    scrollBounds,
    options: {
      loop: loop2
    }
  }, alpha) => {
    const shouldSettle = scrollBody2.settled();
    const withinBounds = !scrollBounds.shouldConstrain();
    const hasSettled = loop2 ? shouldSettle : shouldSettle && withinBounds;
    const hasSettledAndIdle = hasSettled && !dragHandler.pointerDown();
    if (hasSettledAndIdle) animation2.stop();
    const interpolatedLocation = location2.get() * alpha + previousLocation2.get() * (1 - alpha);
    offsetLocation2.set(interpolatedLocation);
    if (loop2) {
      scrollLooper.loop(scrollBody2.direction());
      slideLooper.loop();
    }
    translate.to(offsetLocation2.get());
    if (hasSettledAndIdle) eventHandler2.emit("settle");
    if (!hasSettled) eventHandler2.emit("scroll");
  };
  const animation = Animations(ownerDocument, ownerWindow, () => update(engine), (alpha) => render(engine, alpha));
  const friction = 0.68;
  const startLocation = scrollSnaps[index.get()];
  const location = Vector1D(startLocation);
  const previousLocation = Vector1D(startLocation);
  const offsetLocation = Vector1D(startLocation);
  const target = Vector1D(startLocation);
  const scrollBody = ScrollBody(location, offsetLocation, previousLocation, target, duration, friction);
  const scrollTarget = ScrollTarget(loop, scrollSnaps, contentSize, limit, target);
  const scrollTo = ScrollTo(animation, index, indexPrevious, scrollBody, scrollTarget, target, eventHandler);
  const scrollProgress = ScrollProgress(limit);
  const eventStore = EventStore();
  const slidesInView = SlidesInView(container, slides, eventHandler, inViewThreshold);
  const {
    slideRegistry
  } = SlideRegistry(containSnaps, containScroll, scrollSnaps, scrollContainLimit, slidesToScroll, slideIndexes);
  const slideFocus = SlideFocus(root, slides, slideRegistry, scrollTo, scrollBody, eventStore, eventHandler, watchFocus);
  const engine = {
    ownerDocument,
    ownerWindow,
    eventHandler,
    containerRect,
    slideRects,
    animation,
    axis,
    dragHandler: DragHandler(axis, root, ownerDocument, ownerWindow, target, DragTracker(axis, ownerWindow), location, animation, scrollTo, scrollBody, scrollTarget, index, eventHandler, percentOfView, dragFree, dragThreshold, skipSnaps, friction, watchDrag),
    eventStore,
    percentOfView,
    index,
    indexPrevious,
    limit,
    location,
    offsetLocation,
    previousLocation,
    options,
    resizeHandler: ResizeHandler(container, eventHandler, ownerWindow, slides, axis, watchResize, nodeRects),
    scrollBody,
    scrollBounds: ScrollBounds(limit, offsetLocation, target, scrollBody, percentOfView),
    scrollLooper: ScrollLooper(contentSize, limit, offsetLocation, [location, offsetLocation, previousLocation, target]),
    scrollProgress,
    scrollSnapList: scrollSnaps.map(scrollProgress.get),
    scrollSnaps,
    scrollTarget,
    scrollTo,
    slideLooper: SlideLooper(axis, viewSize, contentSize, slideSizes, slideSizesWithGaps, snaps, scrollSnaps, offsetLocation, slides),
    slideFocus,
    slidesHandler: SlidesHandler(container, eventHandler, watchSlides),
    slidesInView,
    slideIndexes,
    slideRegistry,
    slidesToScroll,
    target,
    translate: Translate(axis, container)
  };
  return engine;
}
function EventHandler() {
  let listeners = {};
  let api;
  function init(emblaApi) {
    api = emblaApi;
  }
  function getListeners(evt) {
    return listeners[evt] || [];
  }
  function emit(evt) {
    getListeners(evt).forEach((e) => e(api, evt));
    return self;
  }
  function on(evt, cb) {
    listeners[evt] = getListeners(evt).concat([cb]);
    return self;
  }
  function off(evt, cb) {
    listeners[evt] = getListeners(evt).filter((e) => e !== cb);
    return self;
  }
  function clear() {
    listeners = {};
  }
  const self = {
    init,
    emit,
    off,
    on,
    clear
  };
  return self;
}
const defaultOptions$1 = {
  align: "center",
  axis: "x",
  container: null,
  slides: null,
  containScroll: "trimSnaps",
  direction: "ltr",
  slidesToScroll: 1,
  inViewThreshold: 0,
  breakpoints: {},
  dragFree: false,
  dragThreshold: 10,
  loop: false,
  skipSnaps: false,
  duration: 25,
  startIndex: 0,
  active: true,
  watchDrag: true,
  watchResize: true,
  watchSlides: true,
  watchFocus: true
};
function OptionsHandler(ownerWindow) {
  function mergeOptions(optionsA, optionsB) {
    return objectsMergeDeep(optionsA, optionsB || {});
  }
  function optionsAtMedia(options) {
    const optionsAtMedia2 = options.breakpoints || {};
    const matchedMediaOptions = objectKeys(optionsAtMedia2).filter((media) => ownerWindow.matchMedia(media).matches).map((media) => optionsAtMedia2[media]).reduce((a, mediaOption) => mergeOptions(a, mediaOption), {});
    return mergeOptions(options, matchedMediaOptions);
  }
  function optionsMediaQueries(optionsList) {
    return optionsList.map((options) => objectKeys(options.breakpoints || {})).reduce((acc, mediaQueries) => acc.concat(mediaQueries), []).map(ownerWindow.matchMedia);
  }
  const self = {
    mergeOptions,
    optionsAtMedia,
    optionsMediaQueries
  };
  return self;
}
function PluginsHandler(optionsHandler) {
  let activePlugins = [];
  function init(emblaApi, plugins) {
    activePlugins = plugins.filter(({
      options
    }) => optionsHandler.optionsAtMedia(options).active !== false);
    activePlugins.forEach((plugin) => plugin.init(emblaApi, optionsHandler));
    return plugins.reduce((map, plugin) => Object.assign(map, {
      [plugin.name]: plugin
    }), {});
  }
  function destroy() {
    activePlugins = activePlugins.filter((plugin) => plugin.destroy());
  }
  const self = {
    init,
    destroy
  };
  return self;
}
function EmblaCarousel(root, userOptions, userPlugins) {
  const ownerDocument = root.ownerDocument;
  const ownerWindow = ownerDocument.defaultView;
  const optionsHandler = OptionsHandler(ownerWindow);
  const pluginsHandler = PluginsHandler(optionsHandler);
  const mediaHandlers = EventStore();
  const eventHandler = EventHandler();
  const {
    mergeOptions,
    optionsAtMedia,
    optionsMediaQueries
  } = optionsHandler;
  const {
    on,
    off,
    emit
  } = eventHandler;
  const reInit = reActivate;
  let destroyed = false;
  let engine;
  let optionsBase = mergeOptions(defaultOptions$1, EmblaCarousel.globalOptions);
  let options = mergeOptions(optionsBase);
  let pluginList = [];
  let pluginApis;
  let container;
  let slides;
  function storeElements() {
    const {
      container: userContainer,
      slides: userSlides
    } = options;
    const customContainer = isString(userContainer) ? root.querySelector(userContainer) : userContainer;
    container = customContainer || root.children[0];
    const customSlides = isString(userSlides) ? container.querySelectorAll(userSlides) : userSlides;
    slides = [].slice.call(customSlides || container.children);
  }
  function createEngine(options2) {
    const engine2 = Engine(root, container, slides, ownerDocument, ownerWindow, options2, eventHandler);
    if (options2.loop && !engine2.slideLooper.canLoop()) {
      const optionsWithoutLoop = Object.assign({}, options2, {
        loop: false
      });
      return createEngine(optionsWithoutLoop);
    }
    return engine2;
  }
  function activate(withOptions, withPlugins) {
    if (destroyed) return;
    optionsBase = mergeOptions(optionsBase, withOptions);
    options = optionsAtMedia(optionsBase);
    pluginList = withPlugins || pluginList;
    storeElements();
    engine = createEngine(options);
    optionsMediaQueries([optionsBase, ...pluginList.map(({
      options: options2
    }) => options2)]).forEach((query) => mediaHandlers.add(query, "change", reActivate));
    if (!options.active) return;
    engine.translate.to(engine.location.get());
    engine.animation.init();
    engine.slidesInView.init();
    engine.slideFocus.init(self);
    engine.eventHandler.init(self);
    engine.resizeHandler.init(self);
    engine.slidesHandler.init(self);
    if (engine.options.loop) engine.slideLooper.loop();
    if (container.offsetParent && slides.length) engine.dragHandler.init(self);
    pluginApis = pluginsHandler.init(self, pluginList);
  }
  function reActivate(withOptions, withPlugins) {
    const startIndex = selectedScrollSnap();
    deActivate();
    activate(mergeOptions({
      startIndex
    }, withOptions), withPlugins);
    eventHandler.emit("reInit");
  }
  function deActivate() {
    engine.dragHandler.destroy();
    engine.eventStore.clear();
    engine.translate.clear();
    engine.slideLooper.clear();
    engine.resizeHandler.destroy();
    engine.slidesHandler.destroy();
    engine.slidesInView.destroy();
    engine.animation.destroy();
    pluginsHandler.destroy();
    mediaHandlers.clear();
  }
  function destroy() {
    if (destroyed) return;
    destroyed = true;
    mediaHandlers.clear();
    deActivate();
    eventHandler.emit("destroy");
    eventHandler.clear();
  }
  function scrollTo(index, jump, direction) {
    if (!options.active || destroyed) return;
    engine.scrollBody.useBaseFriction().useDuration(jump === true ? 0 : options.duration);
    engine.scrollTo.index(index, direction || 0);
  }
  function scrollNext(jump) {
    const next = engine.index.add(1).get();
    scrollTo(next, jump, -1);
  }
  function scrollPrev(jump) {
    const prev = engine.index.add(-1).get();
    scrollTo(prev, jump, 1);
  }
  function canScrollNext() {
    const next = engine.index.add(1).get();
    return next !== selectedScrollSnap();
  }
  function canScrollPrev() {
    const prev = engine.index.add(-1).get();
    return prev !== selectedScrollSnap();
  }
  function scrollSnapList() {
    return engine.scrollSnapList;
  }
  function scrollProgress() {
    return engine.scrollProgress.get(engine.offsetLocation.get());
  }
  function selectedScrollSnap() {
    return engine.index.get();
  }
  function previousScrollSnap() {
    return engine.indexPrevious.get();
  }
  function slidesInView() {
    return engine.slidesInView.get();
  }
  function slidesNotInView() {
    return engine.slidesInView.get(false);
  }
  function plugins() {
    return pluginApis;
  }
  function internalEngine() {
    return engine;
  }
  function rootNode() {
    return root;
  }
  function containerNode() {
    return container;
  }
  function slideNodes() {
    return slides;
  }
  const self = {
    canScrollNext,
    canScrollPrev,
    containerNode,
    internalEngine,
    destroy,
    off,
    on,
    emit,
    plugins,
    previousScrollSnap,
    reInit,
    rootNode,
    scrollNext,
    scrollPrev,
    scrollProgress,
    scrollSnapList,
    scrollTo,
    selectedScrollSnap,
    slideNodes,
    slidesInView,
    slidesNotInView
  };
  activate(userOptions, userPlugins);
  setTimeout(() => eventHandler.emit("init"), 0);
  return self;
}
EmblaCarousel.globalOptions = void 0;
const defaultOptions = {
  active: true,
  breakpoints: {},
  snapped: "is-snapped",
  inView: "is-in-view",
  draggable: "is-draggable",
  dragging: "is-dragging",
  loop: "is-loop"
};
function normalizeClassNames(classNames) {
  const normalized = Array.isArray(classNames) ? classNames : [classNames];
  return normalized.filter(Boolean);
}
function removeClass(node, classNames) {
  if (!node || !classNames.length) return;
  node.classList.remove(...classNames);
}
function addClass(node, classNames) {
  if (!node || !classNames.length) return;
  node.classList.add(...classNames);
}
function ClassNames(userOptions = {}) {
  let options;
  let emblaApi;
  let root;
  let slides;
  let snappedIndexes = [];
  let inViewIndexes = [];
  const selectedEvents = ["select"];
  const draggingEvents = ["pointerDown", "pointerUp"];
  const inViewEvents = ["slidesInView"];
  const classNames = {
    snapped: [],
    inView: [],
    draggable: [],
    dragging: [],
    loop: []
  };
  function init(emblaApiInstance, optionsHandler) {
    emblaApi = emblaApiInstance;
    const {
      mergeOptions,
      optionsAtMedia
    } = optionsHandler;
    const optionsBase = mergeOptions(defaultOptions, ClassNames.globalOptions);
    const allOptions = mergeOptions(optionsBase, userOptions);
    options = optionsAtMedia(allOptions);
    root = emblaApi.rootNode();
    slides = emblaApi.slideNodes();
    const {
      watchDrag,
      loop
    } = emblaApi.internalEngine().options;
    const isDraggable = !!watchDrag;
    if (options.loop && loop) {
      classNames.loop = normalizeClassNames(options.loop);
      addClass(root, classNames.loop);
    }
    if (options.draggable && isDraggable) {
      classNames.draggable = normalizeClassNames(options.draggable);
      addClass(root, classNames.draggable);
    }
    if (options.dragging) {
      classNames.dragging = normalizeClassNames(options.dragging);
      draggingEvents.forEach((evt) => emblaApi.on(evt, toggleDraggingClass));
    }
    if (options.snapped) {
      classNames.snapped = normalizeClassNames(options.snapped);
      selectedEvents.forEach((evt) => emblaApi.on(evt, toggleSnappedClasses));
      toggleSnappedClasses();
    }
    if (options.inView) {
      classNames.inView = normalizeClassNames(options.inView);
      inViewEvents.forEach((evt) => emblaApi.on(evt, toggleInViewClasses));
      toggleInViewClasses();
    }
  }
  function destroy() {
    draggingEvents.forEach((evt) => emblaApi.off(evt, toggleDraggingClass));
    selectedEvents.forEach((evt) => emblaApi.off(evt, toggleSnappedClasses));
    inViewEvents.forEach((evt) => emblaApi.off(evt, toggleInViewClasses));
    removeClass(root, classNames.loop);
    removeClass(root, classNames.draggable);
    removeClass(root, classNames.dragging);
    toggleSlideClasses([], snappedIndexes, classNames.snapped);
    toggleSlideClasses([], inViewIndexes, classNames.inView);
    Object.keys(classNames).forEach((classNameKey) => {
      const key = classNameKey;
      classNames[key] = [];
    });
  }
  function toggleDraggingClass(_, evt) {
    const toggleClass = evt === "pointerDown" ? addClass : removeClass;
    toggleClass(root, classNames.dragging);
  }
  function toggleSlideClasses(addClassIndexes = [], removeClassIndexes = [], classNames2) {
    const removeClassSlides = removeClassIndexes.map((index) => slides[index]);
    const addClassSlides = addClassIndexes.map((index) => slides[index]);
    removeClassSlides.forEach((slide) => removeClass(slide, classNames2));
    addClassSlides.forEach((slide) => addClass(slide, classNames2));
    return addClassIndexes;
  }
  function toggleSnappedClasses() {
    const {
      slideRegistry
    } = emblaApi.internalEngine();
    const newSnappedIndexes = slideRegistry[emblaApi.selectedScrollSnap()];
    snappedIndexes = toggleSlideClasses(newSnappedIndexes, snappedIndexes, classNames.snapped);
  }
  function toggleInViewClasses() {
    const newInViewIndexes = emblaApi.slidesInView();
    inViewIndexes = toggleSlideClasses(newInViewIndexes, inViewIndexes, classNames.inView);
  }
  const self = {
    name: "classNames",
    options: userOptions,
    init,
    destroy
  };
  return self;
}
ClassNames.globalOptions = void 0;
const withEmblaDragClassNames = (plugins = []) => {
  if (plugins.some((plugin) => (plugin == null ? void 0 : plugin.name) === "classNames")) return plugins;
  return [
    ...plugins,
    ClassNames({
      snapped: "",
      inView: "",
      loop: ""
    })
  ];
};
const HybridCarousel = (viewport, options, plugins = []) => {
  var _a, _b, _c, _d;
  const rootNode = viewport.closest(".embla");
  const isMobile = window.innerWidth < 992;
  const disableNative = (rootNode == null ? void 0 : rootNode.dataset.disableNativeScroll) === "true";
  const mobileBreakpoint = ((_a = options == null ? void 0 : options.breakpoints) == null ? void 0 : _a["(max-width: 991.98px)"]) || ((_b = options == null ? void 0 : options.breakpoints) == null ? void 0 : _b["(max-width: 767px)"]);
  const activeOnMobile = mobileBreakpoint ? mobileBreakpoint.active !== false : (options == null ? void 0 : options.active) !== false;
  const isLoop = (options == null ? void 0 : options.loop) === true || mobileBreakpoint && mobileBreakpoint.loop === true;
  const hasAutoplay = plugins && plugins.some((p) => (p == null ? void 0 : p.name) === "autoplay");
  let slidePerView = 1;
  if (rootNode) {
    const rootComponent = rootNode.closest("[data-items-per-row-mobile]");
    if (rootComponent) {
      const spvStr = rootComponent.getAttribute("data-items-per-row-mobile");
      if (spvStr) slidePerView = parseFloat(spvStr) || 1;
    } else {
      const styles = window.getComputedStyle(rootNode);
      const spvStr = styles.getPropertyValue("--slide-per-view-mobile").trim();
      if (spvStr) slidePerView = parseFloat(spvStr) || 1;
    }
  }
  const slides = Array.from(((_c = viewport.firstElementChild) == null ? void 0 : _c.children) || []);
  const hasEnoughSlides = slides.length > slidePerView;
  ((_d = window.Shopify) == null ? void 0 : _d.designMode) === true;
  if (isMobile && !disableNative && activeOnMobile && !isLoop && !hasAutoplay && hasEnoughSlides) {
    if (rootNode) rootNode.classList.add("is-native-scroll");
    return initNativeScroll(viewport, options);
  }
  if (rootNode) rootNode.classList.remove("is-native-scroll");
  const emblaApi = EmblaCarousel(
    viewport,
    options,
    withEmblaDragClassNames(plugins)
  );
  const container = viewport.firstElementChild;
  if (container) {
    const handleFocusin = (e) => {
      var _a2;
      const slide = e.target.closest(".embla__slide");
      if (!slide) return;
      const autoplay = (_a2 = emblaApi.plugins()) == null ? void 0 : _a2.autoplay;
      if (autoplay && typeof autoplay.stop === "function") {
        autoplay.stop();
      }
      const viewportRect = viewport.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const isFullyInView = slideRect.left >= viewportRect.left - 1 && slideRect.right <= viewportRect.right + 1;
      if (!isFullyInView) {
        requestAnimationFrame(() => {
          const slides2 = emblaApi.slideNodes();
          const index = slides2.indexOf(slide);
          if (index > -1) {
            emblaApi.scrollTo(index);
          }
        });
      }
    };
    container.addEventListener("focusin", handleFocusin);
    const destroyEmbla = emblaApi.destroy.bind(emblaApi);
    let destroyed = false;
    emblaApi.destroy = () => {
      if (destroyed) return;
      destroyed = true;
      container.removeEventListener("focusin", handleFocusin);
      destroyEmbla();
    };
  }
  return emblaApi;
};
const prepareEmblaInteraction = (controller) => {
  var _a, _b;
  if (!(controller == null ? void 0 : controller.isConnected)) return false;
  if (controller.emblaApi) return true;
  if (!((_a = controller.nodes) == null ? void 0 : _a.viewport)) return true;
  if (typeof controller._doInitEmbla !== "function") return false;
  (_b = controller._emblaInitObserver) == null ? void 0 : _b.disconnect();
  controller._emblaInitObserver = null;
  controller._emblaInitQueued = false;
  controller._doInitEmbla();
  return !!controller.emblaApi;
};
const addEmblaAutoplayViewportLifecycle = (emblaApi, root, { rootMargin = "300px 0px" } = {}) => {
  var _a, _b, _c;
  const autoplay = (_b = (_a = emblaApi == null ? void 0 : emblaApi.plugins) == null ? void 0 : _a.call(emblaApi)) == null ? void 0 : _b.autoplay;
  if (!autoplay || !root || ((_c = window.Shopify) == null ? void 0 : _c.designMode)) return () => {
  };
  let isNearViewport = false;
  const syncAutoplay = () => {
    var _a2, _b2;
    if (document.hidden || !isNearViewport) {
      (_a2 = autoplay.stop) == null ? void 0 : _a2.call(autoplay);
      return;
    }
    (_b2 = autoplay.play) == null ? void 0 : _b2.call(autoplay);
  };
  const observer = new IntersectionObserver(([entry]) => {
    isNearViewport = entry.isIntersecting;
    syncAutoplay();
  }, { rootMargin });
  observer.observe(root);
  const onVisibilityChange = () => syncAutoplay();
  document.addEventListener("visibilitychange", onVisibilityChange, { passive: true });
  return () => {
    observer.disconnect();
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
};
const debouncedWatchResize = () => {
  let lastCW = 0;
  let emblaRef = null;
  let wasBlocked = false;
  const onResizeSettled = () => {
    if (!wasBlocked || !emblaRef) return;
    wasBlocked = false;
    const cw = window.innerWidth;
    if (cw === lastCW) return;
    lastCW = cw;
    try {
      emblaRef.reInit();
    } catch {
    }
  };
  window.addEventListener("resize-settled", onResizeSettled);
  const watchResize = (emblaApi) => {
    var _a;
    emblaRef = emblaApi;
    if (window.__isResizing) {
      wasBlocked = true;
      return false;
    }
    if ((_a = window.Shopify) == null ? void 0 : _a.designMode) return true;
    const cw = window.innerWidth;
    if (cw === lastCW) return false;
    lastCW = cw;
    return true;
  };
  watchResize.destroy = () => {
    window.removeEventListener("resize-settled", onResizeSettled);
    emblaRef = null;
    wasBlocked = false;
  };
  return watchResize;
};
const addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
  let prevCanPrev = null;
  let prevCanNext = null;
  const prevBtnRoot = prevBtn.closest(".embla__snip-button");
  const nextBtnRoot = nextBtn.closest(".embla__snip-button");
  const cursorNavigationRoot = prevBtn.closest('[data-cursor-navigation="true"]') ?? nextBtn.closest('[data-cursor-navigation="true"]');
  const togglePrevNextBtnsState = () => {
    const canPrev = emblaApi.canScrollPrev();
    const canNext = emblaApi.canScrollNext();
    if (canPrev === prevCanPrev && canNext === prevCanNext) return;
    prevCanPrev = canPrev;
    prevCanNext = canNext;
    prevBtn.toggleAttribute("disabled", !canPrev);
    nextBtn.toggleAttribute("disabled", !canNext);
    prevBtnRoot == null ? void 0 : prevBtnRoot.toggleAttribute("data-disabled", !canPrev);
    nextBtnRoot == null ? void 0 : nextBtnRoot.toggleAttribute("data-disabled", !canNext);
    cursorNavigationRoot == null ? void 0 : cursorNavigationRoot.toggleAttribute(
      "data-cursor-navigation-prev-disabled",
      !canPrev
    );
    cursorNavigationRoot == null ? void 0 : cursorNavigationRoot.toggleAttribute(
      "data-cursor-navigation-next-disabled",
      !canNext
    );
    if (cursorNavigationRoot) {
      window.dispatchEvent(new CustomEvent("cursor:update"));
    }
  };
  togglePrevNextBtnsState();
  emblaApi.on("select", togglePrevNextBtnsState);
  emblaApi.on("reInit", togglePrevNextBtnsState);
  return () => {
    emblaApi.off("select", togglePrevNextBtnsState);
    emblaApi.off("reInit", togglePrevNextBtnsState);
    prevBtn.removeAttribute("disabled");
    nextBtn.removeAttribute("disabled");
    prevBtnRoot == null ? void 0 : prevBtnRoot.removeAttribute("data-disabled");
    nextBtnRoot == null ? void 0 : nextBtnRoot.removeAttribute("data-disabled");
    cursorNavigationRoot == null ? void 0 : cursorNavigationRoot.removeAttribute(
      "data-cursor-navigation-prev-disabled"
    );
    cursorNavigationRoot == null ? void 0 : cursorNavigationRoot.removeAttribute(
      "data-cursor-navigation-next-disabled"
    );
  };
};
const addPrevNextBtnsClickHandlersSSR = (emblaApi, prevBtn, nextBtn) => {
  const scrollPrev = () => {
    requestAnimationFrame(() => {
      var _a, _b;
      emblaApi.scrollPrev();
      if ((_a = emblaApi.plugins()) == null ? void 0 : _a.autoplay) {
        (_b = emblaApi.plugins()) == null ? void 0 : _b.autoplay.reset();
      }
    });
  };
  const scrollNext = () => {
    requestAnimationFrame(() => {
      var _a, _b;
      emblaApi.scrollNext();
      if ((_a = emblaApi.plugins()) == null ? void 0 : _a.autoplay) {
        (_b = emblaApi.plugins()) == null ? void 0 : _b.autoplay.reset();
      }
    });
  };
  prevBtn.addEventListener("click", scrollPrev, false);
  nextBtn.addEventListener("click", scrollNext, false);
  const removeTogglePrevNextBtnsActive = addTogglePrevNextBtnsActive(
    emblaApi,
    prevBtn,
    nextBtn
  );
  return () => {
    removeTogglePrevNextBtnsActive();
    prevBtn.removeEventListener("click", scrollPrev, false);
    nextBtn.removeEventListener("click", scrollNext, false);
  };
};
const addThumbPrevNextBtnsClickHandlers = (emblaApiMain, emblaApiThumb, prevBtn, nextBtn) => {
  const scrollPrev = () => {
    requestAnimationFrame(() => emblaApiMain.scrollPrev());
  };
  const scrollNext = () => {
    requestAnimationFrame(() => emblaApiMain.scrollNext());
  };
  prevBtn.addEventListener("click", scrollPrev, false);
  nextBtn.addEventListener("click", scrollNext, false);
  const removeTogglePrevNextBtnsActive = addTogglePrevNextBtnsActive(
    emblaApiThumb,
    prevBtn,
    nextBtn
  );
  return () => {
    removeTogglePrevNextBtnsActive();
    prevBtn.removeEventListener("click", scrollPrev, false);
    nextBtn.removeEventListener("click", scrollNext, false);
  };
};
const setupEmblaDotAttr = (dotNode, index) => {
  var _a;
  if (!dotNode.hasAttribute("tabindex")) {
    dotNode.setAttribute("tabindex", "0");
  }
  if (!dotNode.hasAttribute("role")) {
    dotNode.setAttribute("role", "button");
  }
  if (!dotNode.hasAttribute("aria-label")) {
    const labelTemplate = ((_a = dotNode.closest(".embla__snip-dots")) == null ? void 0 : _a.dataset.slideLabel) || "__number__";
    dotNode.setAttribute("aria-label", labelTemplate.replace("__number__", String(index + 1)));
  }
  if (!dotNode._hasKeydown) {
    dotNode._hasKeydown = true;
    dotNode.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        dotNode.click();
      }
    });
  }
};
const createDoubleRafScheduler = () => {
  let rafId = 0;
  let version = 0;
  const cancel = () => {
    version += 1;
    cancelAnimationFrame(rafId);
    rafId = 0;
  };
  const schedule = (callback) => {
    cancel();
    const scheduledVersion = version;
    rafId = requestAnimationFrame(() => {
      if (scheduledVersion !== version) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        if (scheduledVersion === version) callback();
      });
    });
  };
  return { cancel, schedule };
};
const getPaginationDirection = (emblaApi, selected, previous, mappedSelected, mappedPrevious, itemCount) => {
  var _a, _b, _c;
  const isLoop = ((_c = (_b = (_a = emblaApi.internalEngine) == null ? void 0 : _a.call(emblaApi)) == null ? void 0 : _b.options) == null ? void 0 : _c.loop) === true;
  const crossesCircularBoundary = isLoop && itemCount > 1 && Math.abs(mappedSelected - mappedPrevious) > itemCount / 2;
  if (crossesCircularBoundary) {
    return mappedSelected < mappedPrevious ? "next" : "prev";
  }
  return selected < previous ? "prev" : "next";
};
const setClassState = (node, className, enabled) => {
  if (node.classList.contains(className) === enabled) return;
  node.classList.toggle(className, enabled);
};
const createDotWindowController = (dotsNode) => {
  const repositionScheduler = createDoubleRafScheduler();
  const mobileQuery = window.matchMedia("(max-width: 991.98px)");
  const dotsRoot = dotsNode.matches(".embla__snip-dots") ? dotsNode : dotsNode.closest(".embla__snip-dots");
  let activeTrackNode = null;
  let currentTrackOffset = null;
  let instantRepositionPending = false;
  const getMaxVisibleDots = () => {
    const value = Number(dotsRoot == null ? void 0 : dotsRoot.dataset.maxVisibleDots);
    return Number.isInteger(value) && value > 0 ? value : null;
  };
  const setDotHiddenState = (node, isHidden) => {
    setClassState(node, "is-hidden", isHidden);
    if (isHidden) {
      if (node.getAttribute("aria-hidden") !== "true") node.setAttribute("aria-hidden", "true");
      if (node.getAttribute("tabindex") !== "-1") node.setAttribute("tabindex", "-1");
      return;
    }
    if (node.hasAttribute("aria-hidden")) node.removeAttribute("aria-hidden");
    if (node.getAttribute("tabindex") === "-1") {
      if (node.hasAttribute("role")) {
        node.setAttribute("tabindex", "0");
      } else {
        node.removeAttribute("tabindex");
      }
    }
  };
  const setTrackNode = (trackNode) => {
    if (trackNode === activeTrackNode) return;
    repositionScheduler.cancel();
    activeTrackNode == null ? void 0 : activeTrackNode.classList.remove("is-instant-reposition");
    activeTrackNode = trackNode;
    currentTrackOffset = null;
    instantRepositionPending = false;
  };
  const setTrackOffset = (trackNode, offset, instant = false) => {
    setTrackNode(trackNode);
    if (!trackNode) return;
    const keepInstant = instant || instantRepositionPending;
    if (currentTrackOffset !== offset) {
      currentTrackOffset = offset;
      trackNode.style.setProperty("--track-offset", offset);
    }
    if (!keepInstant) {
      setClassState(trackNode, "is-instant-reposition", false);
      return;
    }
    instantRepositionPending = true;
    setClassState(trackNode, "is-instant-reposition", true);
    repositionScheduler.schedule(() => {
      setClassState(trackNode, "is-instant-reposition", false);
      instantRepositionPending = false;
    });
  };
  const sync = (dotNodes, selected, previous, dotCount) => {
    const trackNode = dotsNode.querySelector(".embla__dots-track");
    const maxVisibleDots = getMaxVisibleDots();
    const visibleDotCount = Math.max(1, Math.min(dotCount, maxVisibleDots || dotCount));
    const visibleInactiveDots = String(visibleDotCount - 1);
    const visibleCountProperty = mobileQuery.matches ? "--visible-inactive-dots-mobile" : "--visible-inactive-dots-desktop";
    if ((dotsRoot == null ? void 0 : dotsRoot.style.getPropertyValue(visibleCountProperty)) !== visibleInactiveDots) {
      dotsRoot == null ? void 0 : dotsRoot.style.setProperty(visibleCountProperty, visibleInactiveDots);
    }
    const hasSlidingWindow = Boolean(trackNode && maxVisibleDots && dotCount > maxVisibleDots);
    let windowStart = 0;
    let windowEnd = dotCount;
    let lastWindowStart = 0;
    if (hasSlidingWindow) {
      lastWindowStart = dotCount - maxVisibleDots;
      const centerOffset = Math.floor(maxVisibleDots / 2);
      windowStart = Math.max(0, Math.min(selected - centerOffset, lastWindowStart));
      windowEnd = windowStart + maxVisibleDots;
      const previousWindowStart = Math.max(0, Math.min(previous - centerOffset, lastWindowStart));
      const crossesCircularBoundary = Math.abs(selected - previous) > dotCount / 2;
      const skipsMultiplePositions = Math.abs(windowStart - previousWindowStart) > 1;
      setTrackOffset(trackNode, -windowStart, crossesCircularBoundary || skipsMultiplePositions);
    } else {
      setTrackOffset(trackNode, 0);
    }
    dotNodes.forEach((node, index) => {
      const isHidden = hasSlidingWindow && (index < windowStart || index >= windowEnd);
      const isMedium = hasSlidingWindow && !isHidden && (windowStart > 0 && index === windowStart || windowStart < lastWindowStart && index === windowEnd - 1);
      setDotHiddenState(node, isHidden);
      setClassState(node, "is-medium", isMedium);
    });
  };
  const destroy = () => {
    repositionScheduler.cancel();
    activeTrackNode == null ? void 0 : activeTrackNode.classList.remove("is-instant-reposition");
    activeTrackNode = null;
    currentTrackOffset = null;
    instantRepositionPending = false;
  };
  return { sync, destroy };
};
const addInstagramDotBtnsAndClickHandlers = (emblaApi, dotsNode, originalSlideCount = null) => {
  let dotNodes = [];
  const selectionScheduler = createDoubleRafScheduler();
  const dotWindowController = createDotWindowController(dotsNode);
  const getDotCount = () => originalSlideCount || emblaApi.scrollSnapList().length;
  const cursorAttr = getThemeSettings().useCustomCursor ? ' data-cursor="hide"' : "";
  const addDotBtnsWithClickHandlers = () => {
    selectionScheduler.cancel();
    const dotCount = getDotCount();
    const dotsMarkup = Array(dotCount).fill(0).map(
      (_, index) => `<div class="embla__dot"${cursorAttr} tabindex="0" role="button"><div class="embla__dot-inner"></div></div>`
    ).join("");
    dotsNode.innerHTML = `<div class="embla__dots-track">${dotsMarkup}</div>`;
    dotNodes = Array.from(dotsNode.querySelectorAll(".embla__dot"));
    dotNodes.forEach((dotNode, index) => setupEmblaDotAttr(dotNode, index));
    dotNodes.forEach((dotNode, index) => {
      dotNode.addEventListener("click", () => {
        requestAnimationFrame(() => {
          let targetIndex = index;
          if (originalSlideCount) {
            const current = emblaApi.selectedScrollSnap();
            const totalSnaps = emblaApi.scrollSnapList().length;
            let closestDist = Infinity;
            for (let i = index; i < totalSnaps; i += originalSlideCount) {
              const dist = Math.abs(current - i);
              const wrappedDist = totalSnaps - dist;
              const actualDist = Math.min(dist, wrappedDist);
              if (actualDist < closestDist) {
                closestDist = actualDist;
                targetIndex = i;
              }
            }
          }
          emblaApi.scrollTo(targetIndex);
        });
      }, false);
    });
  };
  const toggleDotBtnsActive = () => {
    selectionScheduler.cancel();
    const rawPrevious = emblaApi.previousScrollSnap();
    const rawSelected = emblaApi.selectedScrollSnap();
    const dotCount = getDotCount();
    const previous = originalSlideCount ? rawPrevious % dotCount : rawPrevious;
    const selected = originalSlideCount ? rawSelected % dotCount : rawSelected;
    dotWindowController.sync(dotNodes, selected, previous, dotCount);
    const direction = getPaginationDirection(emblaApi, rawSelected, rawPrevious, selected, previous, dotCount);
    dotNodes.forEach((node) => {
      node.classList.remove("embla__dot--selected", "is-leaving-next", "is-leaving-prev", "is-entering-prev", "no-transition");
    });
    if (selected === previous) {
      if (dotNodes[selected]) {
        dotNodes[selected].classList.add("embla__dot--selected");
      }
      return;
    }
    if (dotNodes[previous]) {
      dotNodes[previous].classList.add(`is-leaving-${direction}`);
    }
    if (dotNodes[selected]) {
      if (direction === "prev") {
        const targetNode = dotNodes[selected];
        targetNode.classList.add("is-entering-prev", "no-transition");
        selectionScheduler.schedule(() => {
          const currentRaw = emblaApi.selectedScrollSnap();
          const current = originalSlideCount ? currentRaw % getDotCount() : currentRaw;
          if (current !== selected || dotNodes[selected] !== targetNode) return;
          targetNode.classList.remove("no-transition", "is-entering-prev");
          targetNode.classList.add("embla__dot--selected");
        });
      } else {
        dotNodes[selected].classList.add("embla__dot--selected");
      }
    }
  };
  addDotBtnsWithClickHandlers();
  emblaApi.on("select", toggleDotBtnsActive);
  emblaApi.on("reinit", addDotBtnsWithClickHandlers);
  emblaApi.on("reinit", toggleDotBtnsActive);
  toggleDotBtnsActive();
  return () => {
    selectionScheduler.cancel();
    dotWindowController.destroy();
    emblaApi.off("select", toggleDotBtnsActive);
    emblaApi.off("reinit", addDotBtnsWithClickHandlers);
    emblaApi.off("reinit", toggleDotBtnsActive);
    dotsNode.innerHTML = "";
  };
};
const addAutoplayProgressListeners = (emblaApi, getActiveBarNode) => {
  let rafId = 0;
  const updateProgress = () => {
    var _a;
    rafId = 0;
    const autoplay = (_a = emblaApi == null ? void 0 : emblaApi.plugins()) == null ? void 0 : _a.autoplay;
    if (!autoplay) return;
    const barNode = getActiveBarNode();
    if (!barNode) return;
    const inner = barNode.querySelector(".embla__bar-inner");
    if (!inner) return;
    const timeUntilNext = autoplay.timeUntilNext();
    const delay = autoplay.options.delay;
    if (timeUntilNext === null) return;
    const progress = 1 - timeUntilNext / delay;
    inner.style.setProperty("--autoplay-progress", progress);
    rafId = requestAnimationFrame(updateProgress);
  };
  const restartProgress = () => {
    cancelAnimationFrame(rafId);
    rafId = 0;
    updateProgress();
  };
  const resetProgress = () => {
    cancelAnimationFrame(rafId);
    rafId = 0;
    const barNode = getActiveBarNode();
    if (!barNode) return;
    const inner = barNode.querySelector(".embla__bar-inner");
    if (inner) {
      inner.style.setProperty("--autoplay-progress", 0);
    }
  };
  const onVisibilityChange = () => {
    if (document.hidden) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    } else {
      if (!rafId) restartProgress();
    }
  };
  document.addEventListener("visibilitychange", onVisibilityChange, { passive: true });
  updateProgress();
  emblaApi.on("autoplay:timerset", restartProgress).on("autoplay:timerstopped", resetProgress).on("autoplay:reset", resetProgress);
  return () => {
    cancelAnimationFrame(rafId);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    emblaApi.off("autoplay:timerset", restartProgress).off("autoplay:timerstopped", resetProgress).off("autoplay:reset", resetProgress);
  };
};
const addBarsBtnsAndClickHandlersSSR = (emblaApi, barsNode, originalSlideCount = null) => {
  let barNodes = [];
  let selectedNode = 0;
  let scrollHandlers = [];
  let autoplayCleanup = null;
  const selectionScheduler = createDoubleRafScheduler();
  const getBarCount = () => originalSlideCount || emblaApi.scrollSnapList().length;
  const setupClickHandlers = () => {
    var _a;
    selectionScheduler.cancel();
    if (autoplayCleanup) {
      autoplayCleanup();
      autoplayCleanup = null;
    }
    barNodes.forEach((barNode, index) => {
      if (scrollHandlers[index]) {
        barNode.removeEventListener("click", scrollHandlers[index]);
      }
    });
    scrollHandlers = [];
    barNodes = Array.from(barsNode.querySelectorAll(".embla__bar"));
    const actualSnapCount = originalSlideCount || emblaApi.scrollSnapList().length;
    barNodes.forEach((barNode, index) => {
      if (index >= actualSnapCount) {
        barNode.classList.add("hidden");
      } else {
        barNode.classList.remove("hidden");
      }
    });
    const scrollTo = (index) => {
      requestAnimationFrame(() => {
        var _a2;
        const autoplay2 = (_a2 = emblaApi.plugins()) == null ? void 0 : _a2.autoplay;
        emblaApi.scrollTo(index);
        if (autoplay2) {
          autoplay2.reset();
        }
      });
    };
    barNodes.forEach((barNode, index) => {
      const handler = () => scrollTo(index);
      scrollHandlers.push(handler);
      barNode.addEventListener("click", handler);
    });
    const autoplay = (_a = emblaApi == null ? void 0 : emblaApi.plugins()) == null ? void 0 : _a.autoplay;
    if (autoplay) {
      barsNode.classList.add("autoplay");
      barsNode.classList.remove("non-autoplay");
      if (barNodes[selectedNode]) {
        barNodes[selectedNode].classList.add("embla__bar--selected");
      }
      autoplayCleanup = addAutoplayProgressListeners(
        emblaApi,
        () => barNodes[selectedNode]
      );
    } else {
      barsNode.classList.add("non-autoplay");
      barsNode.classList.remove("autoplay");
    }
  };
  const toggleBarBtnsActive = () => {
    selectionScheduler.cancel();
    const selected = emblaApi.selectedScrollSnap();
    const previous = emblaApi.previousScrollSnap();
    const barCount = getBarCount();
    const mappedSelected = originalSlideCount ? selected % barCount : selected;
    const mappedPrevious = originalSlideCount ? previous % barCount : previous;
    const direction = getPaginationDirection(emblaApi, selected, previous, mappedSelected, mappedPrevious, barCount);
    barNodes.forEach((node) => {
      node.classList.remove("embla__bar--selected", "is-leaving-next", "is-leaving-prev", "is-entering-prev", "no-transition");
    });
    if (selected === previous) {
      if (barNodes[mappedSelected]) {
        barNodes[mappedSelected].classList.add("embla__bar--selected");
      }
      selectedNode = mappedSelected;
      return;
    }
    if (barNodes[mappedPrevious]) {
      barNodes[mappedPrevious].classList.add(`is-leaving-${direction}`);
    }
    if (barNodes[mappedSelected]) {
      if (direction === "prev") {
        const targetNode = barNodes[mappedSelected];
        targetNode.classList.add("is-entering-prev", "no-transition");
        selectionScheduler.schedule(() => {
          const currentRaw = emblaApi.selectedScrollSnap();
          const current = originalSlideCount ? currentRaw % getBarCount() : currentRaw;
          if (current !== mappedSelected || barNodes[mappedSelected] !== targetNode) return;
          targetNode.classList.remove("no-transition", "is-entering-prev");
          targetNode.classList.add("embla__bar--selected");
        });
      } else {
        barNodes[mappedSelected].classList.add("embla__bar--selected");
      }
    }
    selectedNode = mappedSelected;
  };
  setupClickHandlers();
  emblaApi.on("select", toggleBarBtnsActive);
  emblaApi.on("reinit", setupClickHandlers);
  emblaApi.on("reinit", toggleBarBtnsActive);
  toggleBarBtnsActive();
  return () => {
    selectionScheduler.cancel();
    if (autoplayCleanup) autoplayCleanup();
    emblaApi.off("select", toggleBarBtnsActive);
    emblaApi.off("reinit", setupClickHandlers);
    emblaApi.off("reinit", toggleBarBtnsActive);
    barNodes.forEach((barNode, index) => {
      if (scrollHandlers[index]) {
        barNode.removeEventListener("click", scrollHandlers[index]);
      }
    });
    scrollHandlers = [];
  };
};
const addAutoplayProgressDotListeners = (emblaApi, getActiveBarNode) => {
  let rafId = 0;
  const updateProgress = () => {
    var _a;
    rafId = 0;
    const autoplay = (_a = emblaApi == null ? void 0 : emblaApi.plugins()) == null ? void 0 : _a.autoplay;
    if (!autoplay) return;
    const barNode = getActiveBarNode();
    if (!barNode) return;
    const dot = barNode.querySelector(".embla__dot-inner, .embla__dot__inner");
    if (!dot) return;
    const timeUntilNext = autoplay.timeUntilNext();
    const delay = autoplay.options.delay;
    if (timeUntilNext === null) return;
    const progress = 1 - timeUntilNext / delay;
    dot.style.setProperty("--autoplay-progress", progress);
    rafId = requestAnimationFrame(updateProgress);
  };
  const restartProgress = () => {
    cancelAnimationFrame(rafId);
    rafId = 0;
    updateProgress();
  };
  const resetProgress = () => {
    cancelAnimationFrame(rafId);
    rafId = 0;
    const barNode = getActiveBarNode();
    if (!barNode) return;
    const dot = barNode.querySelector(".embla__dot-inner, .embla__dot__inner");
    if (dot) dot.style.setProperty("--autoplay-progress", 0);
  };
  const onVisibilityChange = () => {
    if (document.hidden) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    } else {
      if (!rafId) restartProgress();
    }
  };
  document.addEventListener("visibilitychange", onVisibilityChange, { passive: true });
  updateProgress();
  emblaApi.on("autoplay:timerset", restartProgress).on("autoplay:timerstopped", resetProgress).on("autoplay:reset", resetProgress);
  return () => {
    cancelAnimationFrame(rafId);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    emblaApi.off("autoplay:timerset", restartProgress).off("autoplay:timerstopped", resetProgress).off("autoplay:reset", resetProgress);
  };
};
const addDotsBtnsAndClickHandlersSSR = (emblaApi, dotsNode, originalSlideCount = null) => {
  let dotNodes = [];
  let selectedNode = 0;
  let scrollHandlers = [];
  let autoplayCleanup = null;
  const selectionScheduler = createDoubleRafScheduler();
  const dotWindowController = createDotWindowController(dotsNode);
  const getDotCount = () => originalSlideCount || emblaApi.scrollSnapList().length;
  const setupClickHandlers = () => {
    var _a;
    selectionScheduler.cancel();
    if (autoplayCleanup) {
      autoplayCleanup();
      autoplayCleanup = null;
    }
    dotNodes.forEach((dotNode, index) => {
      if (scrollHandlers[index]) {
        dotNode.removeEventListener("click", scrollHandlers[index]);
      }
    });
    scrollHandlers = [];
    dotNodes = Array.from(dotsNode.querySelectorAll(".embla__dot"));
    onFirstInteraction(() => {
      dotNodes.forEach((dotNode, index) => setupEmblaDotAttr(dotNode, index));
    });
    const actualSnapCount = originalSlideCount || emblaApi.scrollSnapList().length;
    dotNodes.forEach((dotNode, index) => {
      if (index >= actualSnapCount) {
        dotNode.classList.add("hidden");
      } else {
        dotNode.classList.remove("hidden");
      }
    });
    const scrollTo = (index) => {
      requestAnimationFrame(() => {
        var _a2;
        const autoplay2 = (_a2 = emblaApi.plugins()) == null ? void 0 : _a2.autoplay;
        let targetIndex = index;
        if (originalSlideCount) {
          const current = emblaApi.selectedScrollSnap();
          const totalSnaps = emblaApi.scrollSnapList().length;
          let closestDist = Infinity;
          for (let i = index; i < totalSnaps; i += originalSlideCount) {
            const dist = Math.abs(current - i);
            const wrappedDist = totalSnaps - dist;
            const actualDist = Math.min(dist, wrappedDist);
            if (actualDist < closestDist) {
              closestDist = actualDist;
              targetIndex = i;
            }
          }
        }
        emblaApi.scrollTo(targetIndex);
        if (autoplay2) {
          autoplay2.reset();
        }
      });
    };
    dotNodes.forEach((dotNode, index) => {
      const handler = () => scrollTo(index);
      scrollHandlers.push(handler);
      dotNode.addEventListener("click", handler);
    });
    const autoplay = (_a = emblaApi == null ? void 0 : emblaApi.plugins()) == null ? void 0 : _a.autoplay;
    if (autoplay) {
      dotsNode.classList.add("autoplay");
      dotsNode.classList.remove("non-autoplay");
      if (dotNodes[selectedNode]) {
        dotNodes[selectedNode].classList.add("embla__dot--selected");
      }
      autoplayCleanup = addAutoplayProgressDotListeners(
        emblaApi,
        () => dotNodes[selectedNode]
      );
    } else {
      dotsNode.classList.add("non-autoplay");
      dotsNode.classList.remove("autoplay");
    }
  };
  const toggleDotBtnsActive = () => {
    selectionScheduler.cancel();
    const selected = emblaApi.selectedScrollSnap();
    const previous = emblaApi.previousScrollSnap();
    const dotCount = getDotCount();
    const mappedSelected = originalSlideCount ? selected % dotCount : selected;
    const mappedPrevious = originalSlideCount ? previous % dotCount : previous;
    dotWindowController.sync(dotNodes, mappedSelected, mappedPrevious, dotCount);
    const direction = getPaginationDirection(emblaApi, selected, previous, mappedSelected, mappedPrevious, dotCount);
    dotNodes.forEach((node) => {
      node.classList.remove("embla__dot--selected", "is-leaving-next", "is-leaving-prev", "is-entering-prev", "no-transition");
    });
    if (selected === previous) {
      if (dotNodes[mappedSelected]) {
        dotNodes[mappedSelected].classList.add("embla__dot--selected");
      }
      selectedNode = mappedSelected;
      return;
    }
    if (dotNodes[mappedPrevious]) {
      dotNodes[mappedPrevious].classList.add(`is-leaving-${direction}`);
    }
    if (dotNodes[mappedSelected]) {
      if (direction === "prev") {
        const targetNode = dotNodes[mappedSelected];
        targetNode.classList.add("is-entering-prev", "no-transition");
        selectionScheduler.schedule(() => {
          const currentRaw = emblaApi.selectedScrollSnap();
          const current = originalSlideCount ? currentRaw % getDotCount() : currentRaw;
          if (current !== mappedSelected || dotNodes[mappedSelected] !== targetNode) return;
          targetNode.classList.remove("no-transition", "is-entering-prev");
          targetNode.classList.add("embla__dot--selected");
        });
      } else {
        dotNodes[mappedSelected].classList.add("embla__dot--selected");
      }
    }
    selectedNode = mappedSelected;
  };
  setupClickHandlers();
  emblaApi.on("select", toggleDotBtnsActive);
  emblaApi.on("reinit", setupClickHandlers);
  emblaApi.on("reinit", toggleDotBtnsActive);
  toggleDotBtnsActive();
  return () => {
    selectionScheduler.cancel();
    dotWindowController.destroy();
    if (autoplayCleanup) autoplayCleanup();
    emblaApi.off("select", toggleDotBtnsActive);
    emblaApi.off("reinit", setupClickHandlers);
    emblaApi.off("reinit", toggleDotBtnsActive);
    dotNodes.forEach((dotNode, index) => {
      if (scrollHandlers[index]) {
        dotNode.removeEventListener("click", scrollHandlers[index]);
      }
    });
    scrollHandlers = [];
  };
};
const addLayoutShiftPrevention = (emblaApi, emblaWrapper) => {
  if (!emblaApi || !emblaWrapper) {
    return () => {
    };
  }
  const onInit = () => {
    var _a, _b;
    emblaWrapper.classList.add("is-initialized");
    if (emblaApi.slideNodes().length <= 1) {
      emblaWrapper.classList.add("is-single-slide");
      (_a = emblaApi.containerNode()) == null ? void 0 : _a.classList.add("is-single-slide");
    } else {
      emblaWrapper.classList.remove("is-single-slide");
      (_b = emblaApi.containerNode()) == null ? void 0 : _b.classList.remove("is-single-slide");
    }
  };
  const onDestroy = () => {
    var _a;
    emblaWrapper.classList.remove("is-initialized", "is-single-slide");
    (_a = emblaApi.containerNode()) == null ? void 0 : _a.classList.remove("is-single-slide");
  };
  emblaApi.on("init", onInit).on("reinit", onInit);
  return () => {
    emblaApi.off("init", onInit).off("reinit", onInit);
    onDestroy();
  };
};
const duplicateSlidesForLoop = (emblaApi, emblaContainer, maxIterations = 5, sanitizeClone = null) => {
  var _a, _b;
  const originalSlideCount = emblaContainer.children.length;
  let iterations = 0;
  while (!((_b = (_a = emblaApi == null ? void 0 : emblaApi.internalEngine()) == null ? void 0 : _a.slideLooper) == null ? void 0 : _b.canLoop()) && iterations < maxIterations) {
    const currentSlides = Array.from(emblaContainer.children);
    const frag = document.createDocumentFragment();
    currentSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.classList.remove("is-snapped");
      clone.querySelectorAll("img").forEach((img) => {
        img.setAttribute("loaded", "");
        if (img.parentElement) img.parentElement.removeAttribute("data-lazy-loading");
      });
      sanitizeClone == null ? void 0 : sanitizeClone(clone, slide);
      frag.appendChild(clone);
    });
    emblaContainer.appendChild(frag);
    emblaApi == null ? void 0 : emblaApi.reInit();
    iterations++;
  }
  return originalSlideCount;
};
const extractSlideColorSchemes = (emblaContainer) => {
  const slides = Array.from(emblaContainer.children);
  const colorSchemes = slides.map((slide) => {
    const colorClass = Array.from(slide.classList).find((cls) => cls.startsWith("color-"));
    return colorClass || null;
  });
  return colorSchemes;
};
const addColorSchemeSyncHandlers = (emblaApi, containers, slideColorSchemes, originalSlideCount = null) => {
  const validContainers = (Array.isArray(containers) ? containers : [containers]).filter(Boolean);
  if (!emblaApi || validContainers.length === 0 || !slideColorSchemes || slideColorSchemes.length === 0) {
    return () => {
    };
  }
  let currentColorScheme = null;
  const updateColorScheme = () => {
    const selectedIndex = emblaApi.selectedScrollSnap();
    const originalIndex = originalSlideCount ? selectedIndex % originalSlideCount : selectedIndex;
    const newColorScheme = slideColorSchemes[originalIndex];
    if (newColorScheme === currentColorScheme) {
      return;
    }
    if (currentColorScheme) {
      validContainers.forEach((container) => container.classList.remove(currentColorScheme));
    }
    if (newColorScheme) {
      validContainers.forEach((container) => container.classList.add(newColorScheme));
    }
    currentColorScheme = newColorScheme;
  };
  emblaApi.on("init", updateColorScheme).on("reinit", updateColorScheme).on("select", updateColorScheme);
  return () => {
    emblaApi.off("init", updateColorScheme).off("reinit", updateColorScheme).off("select", updateColorScheme);
  };
};
const addNumberPagination = (emblaApi, paginationContainer) => {
  if (!emblaApi || !paginationContainer) {
    return () => {
    };
  }
  const currentEl = paginationContainer.querySelector(".embla__number-current");
  const totalEl = paginationContainer.querySelector(".embla__number-total");
  if (!currentEl || !totalEl) {
    return () => {
    };
  }
  const updateNumbers = () => {
    const currentSlide = emblaApi.selectedScrollSnap() + 1;
    const totalSlides = emblaApi.scrollSnapList().length;
    currentEl.textContent = currentSlide;
    totalEl.textContent = totalSlides;
  };
  updateNumbers();
  emblaApi.on("select", updateNumbers).on("init", updateNumbers).on("reinit", updateNumbers);
  return () => {
    emblaApi.off("select", updateNumbers).off("init", updateNumbers).off("reinit", updateNumbers);
  };
};
const addThumbButtonClickHandlers = (emblaApiMain, emblaApiThumb) => {
  const slidesThumbs = emblaApiThumb.slideNodes();
  const scrollToIndex = slidesThumbs.map(
    (_, index) => () => requestAnimationFrame(() => emblaApiMain.scrollTo(index))
  );
  const activateOnKeydown = slidesThumbs.map(
    (_, index) => (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      scrollToIndex[index]();
    }
  );
  slidesThumbs.forEach((slideNode, index) => {
    slideNode.addEventListener("click", scrollToIndex[index], false);
    slideNode.addEventListener("keydown", activateOnKeydown[index], false);
  });
  return () => {
    slidesThumbs.forEach((slideNode, index) => {
      slideNode.removeEventListener("click", scrollToIndex[index], false);
      slideNode.removeEventListener("keydown", activateOnKeydown[index], false);
    });
  };
};
const addToggleThumbButtonsActive = (emblaApiMain, emblaApiThumb) => {
  const slidesThumbs = emblaApiThumb.slideNodes();
  let isThumbInteracting = false;
  let lastMainSelected = null;
  let syncRaf = null;
  const cancelPendingSync = () => {
    if (syncRaf === null) return;
    cancelAnimationFrame(syncRaf);
    syncRaf = null;
  };
  const onThumbPointerDown = () => {
    isThumbInteracting = true;
    cancelPendingSync();
  };
  const onThumbInteractionEnd = () => {
    isThumbInteracting = false;
  };
  const toggleThumbBtnsState = () => {
    const previous = emblaApiMain.previousScrollSnap();
    const selected = emblaApiMain.selectedScrollSnap();
    const selectionChanged = selected !== lastMainSelected;
    const previousThumb = slidesThumbs[previous];
    const selectedThumb = slidesThumbs[selected];
    previousThumb == null ? void 0 : previousThumb.classList.remove("embla-thumbs__slide--selected");
    if (previousThumb && previousThumb !== selectedThumb && previousThumb.hasAttribute("aria-current")) {
      previousThumb.removeAttribute("aria-current");
    }
    selectedThumb == null ? void 0 : selectedThumb.classList.add("embla-thumbs__slide--selected");
    if (selectedThumb && selectedThumb.getAttribute("aria-current") !== "true") {
      selectedThumb.setAttribute("aria-current", "true");
    }
    lastMainSelected = selected;
    if (!selectionChanged || isThumbInteracting || emblaApiThumb.slidesInView().includes(selected)) return;
    cancelPendingSync();
    syncRaf = requestAnimationFrame(() => {
      syncRaf = null;
      if (isThumbInteracting || emblaApiMain.selectedScrollSnap() !== selected) return;
      emblaApiThumb.scrollTo(selected);
    });
  };
  emblaApiMain.on("select", toggleThumbBtnsState);
  emblaApiThumb.on("pointerDown", onThumbPointerDown).on("pointerUp", onThumbInteractionEnd).on("settle", onThumbInteractionEnd);
  toggleThumbBtnsState();
  return () => {
    emblaApiMain.off("select", toggleThumbBtnsState);
    emblaApiThumb.off("pointerDown", onThumbPointerDown).off("pointerUp", onThumbInteractionEnd).off("settle", onThumbInteractionEnd);
    cancelPendingSync();
  };
};
const emblaExtension = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  HybridCarousel,
  addAutoplayProgressDotListeners,
  addAutoplayProgressListeners,
  addBarsBtnsAndClickHandlersSSR,
  addColorSchemeSyncHandlers,
  addDotsBtnsAndClickHandlersSSR,
  addEmblaAutoplayViewportLifecycle,
  addInstagramDotBtnsAndClickHandlers,
  addLayoutShiftPrevention,
  addNumberPagination,
  addPrevNextBtnsClickHandlersSSR,
  addThumbButtonClickHandlers,
  addThumbPrevNextBtnsClickHandlers,
  addToggleThumbButtonsActive,
  debouncedWatchResize,
  duplicateSlidesForLoop,
  extractSlideColorSchemes,
  prepareEmblaInteraction,
  withEmblaDragClassNames
}, Symbol.toStringTag, { value: "Module" }));
export {
  ClassNames as C,
  EmblaCarousel as E,
  HybridCarousel as H,
  addPrevNextBtnsClickHandlersSSR as a,
  duplicateSlidesForLoop as b,
  addLayoutShiftPrevention as c,
  debouncedWatchResize as d,
  extractSlideColorSchemes as e,
  addDotsBtnsAndClickHandlersSSR as f,
  addColorSchemeSyncHandlers as g,
  addInstagramDotBtnsAndClickHandlers as h,
  addEmblaAutoplayViewportLifecycle as i,
  addNumberPagination as j,
  addThumbPrevNextBtnsClickHandlers as k,
  addThumbButtonClickHandlers as l,
  addToggleThumbButtonsActive as m,
  addBarsBtnsAndClickHandlersSSR as n,
  emblaExtension as o,
  prepareEmblaInteraction as p,
  withEmblaDragClassNames as w
};
